import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  AlertTriangle, Coins, RefreshCw, Search, ShieldCheck, ShieldAlert,
  TrendingUp, Wallet, Zap, ExternalLink, Check, X
} from "lucide-react";

/**
 * Admin → CAUL Economy tab
 *
 * Operator dashboard for the on-chain CAUL token loop. Surfaces:
 *   • Live DEX price (CAUL/XRP) — auto-refreshes every 60s
 *   • Hot wallet health (XRP + CAUL balance, threshold flags, last sweep)
 *   • Sweep controls — dry-run preview + manual execute
 *   • Pending payouts (number of users + total CAUL owed)
 *   • OpenSanctions AML screening tool
 */
export default function AdminCaul() {
  return (
    <div className="space-y-8" data-testid="admin-caul">
      <div>
        <div className="label-mystic flex items-center gap-2"><Coins className="w-3 h-3" /> CAUL Economy</div>
        <h2 className="font-serif text-3xl mt-2">Token, sweep &amp; AML controls</h2>
        <p className="text-foreground/60 text-sm mt-1 max-w-2xl">
          Live XRPL DEX price, operational wallet health, daily reward sweep, and OpenSanctions screening — all the levers
          you'll touch when running the CAUL economy.
        </p>
      </div>
      <PriceCard />
      <WalletHealthCard />
      <SweepCard />
      <AmlScreenCard />
    </div>
  );
}

function PriceCard() {
  const [price, setPrice] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/caul/price");
      setPrice(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load price");
    }
  };
  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/caul/price/refresh");
      setPrice(data);
      toast.success(data.stale ? "Order book empty — no fresh price available" : "Price refreshed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally { setBusy(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);

  if (!price) return <div className="text-foreground/50 text-sm">Loading CAUL price…</div>;
  const stale = price.stale === true;

  return (
    <div className="border border-secondary/15 bg-card/40 p-5 space-y-3" data-testid="caul-price-card">
      <div className="flex items-start justify-between">
        <div>
          <div className="label-mystic flex items-center gap-2"><TrendingUp className="w-3 h-3" /> Live DEX Price</div>
          <div className="text-foreground/60 text-xs mt-1">{price.pair || `CAUL/XRP`}</div>
        </div>
        <button
          onClick={refresh}
          disabled={busy}
          className="px-3 py-2 text-xs uppercase tracking-[0.18em] border border-secondary/30 text-secondary hover:bg-secondary/10 disabled:opacity-50 flex items-center gap-2"
          data-testid="caul-price-refresh"
        >
          <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>
      <div className="flex items-end gap-6 flex-wrap">
        <div>
          <div className="font-serif text-4xl text-foreground">
            {price.quotePrice != null ? price.quotePrice.toFixed(8) : "—"}{" "}
            <span className="text-foreground/40 text-sm">{price.quoteCurrency || "XRP"} / CAUL</span>
          </div>
          {stale && (
            <div className="text-amber-400 text-xs mt-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              {price.note || "Stale — empty order book"}
            </div>
          )}
        </div>
        {price.bid && price.ask && (
          <div className="text-xs space-y-1">
            <div className="text-foreground/50">Bid · <span className="text-foreground/80 font-mono">{price.bid.toFixed(8)}</span></div>
            <div className="text-foreground/50">Ask · <span className="text-foreground/80 font-mono">{price.ask.toFixed(8)}</span></div>
            <div className="text-foreground/50">Spread · <span className="text-foreground/80 font-mono">{price.spreadPct?.toFixed(3)}%</span></div>
          </div>
        )}
      </div>
      <div className="text-foreground/40 text-xs">
        Updated {price.updatedAt ? new Date(price.updatedAt).toLocaleString() : "—"}
      </div>
    </div>
  );
}

function WalletHealthCard() {
  const [h, setH] = useState(null);
  const load = async () => {
    try {
      const { data } = await api.get("/admin/caul/wallet-health");
      setH(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load wallet health");
    }
  };
  useEffect(() => { load(); const t = setInterval(load, 120_000); return () => clearInterval(t); }, []);
  if (!h) return <div className="text-foreground/50 text-sm">Loading wallet health…</div>;

  if (!h.configured) {
    return (
      <div className="border border-amber-500/50 bg-amber-500/5 p-4 text-amber-100 text-sm flex items-center gap-2" data-testid="caul-wallet-not-configured">
        <AlertTriangle className="w-4 h-4 text-amber-400" />
        Operational wallet not configured. Set CAUL_OPERATIONAL_ADDRESS in env.
      </div>
    );
  }

  return (
    <div className="border border-secondary/15 bg-card/40 p-5 space-y-4" data-testid="caul-wallet-health-card">
      <div className="flex items-center justify-between">
        <div className="label-mystic flex items-center gap-2"><Wallet className="w-3 h-3" /> Hot Wallet Health</div>
        <a
          href={`https://livenet.xrpl.org/accounts/${h.operationalAddress}`}
          target="_blank" rel="noreferrer"
          className="text-foreground/50 hover:text-secondary text-xs flex items-center gap-1"
          data-testid="caul-wallet-explorer-link"
        >
          {h.operationalAddress.slice(0, 6)}…{h.operationalAddress.slice(-6)} <ExternalLink className="w-3 h-3" />
        </a>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile
          label="XRP"
          value={h.xrpBalance != null ? h.xrpBalance.toFixed(4) : "—"}
          subtitle={`min ${h.thresholds.minXrp}`}
          ring={h.lowXrp ? "bad" : "good"}
          testid="caul-xrp-tile"
        />
        <Tile
          label="CAUL"
          value={h.caulBalance != null ? Math.round(h.caulBalance).toLocaleString() : "—"}
          subtitle={`min ${h.thresholds.minCaul.toLocaleString()}`}
          ring={h.lowCaul ? "bad" : "good"}
          testid="caul-caul-tile"
        />
        <Tile
          label="Pending users"
          value={h.pending.users}
          subtitle={`${h.pending.total.toLocaleString()} CAUL owed`}
          testid="caul-pending-tile"
        />
        <Tile
          label="Last sweep"
          value={h.lastSweep?.ranAt ? new Date(h.lastSweep.ranAt).toLocaleDateString() : "Never"}
          subtitle={h.lastSweep?.swept != null
            ? `${h.lastSweep.swept} swept · ${h.lastSweep.errored || 0} errored`
            : "—"}
          testid="caul-sweep-tile"
        />
      </div>
      {h.lowXrp && (
        <div className="border border-rose-500/50 bg-rose-500/5 text-rose-200 text-sm px-4 py-3 flex items-start gap-2" data-testid="caul-low-xrp-alert">
          <AlertTriangle className="w-4 h-4 text-rose-400 mt-0.5 shrink-0" />
          <div>
            <b>Hot wallet XRP is below threshold.</b>{" "}
            Top up <code className="bg-rose-950/40 px-1">{h.operationalAddress}</code> with at least {h.thresholds.minXrp - (h.xrpBalance || 0)} XRP so sweeps can pay tx fees.
          </div>
        </div>
      )}
      {h.rpcError && (
        <div className="text-amber-400 text-xs flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> RPC error: {h.rpcError}
        </div>
      )}
    </div>
  );
}

function SweepCard() {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTyped, setConfirmTyped] = useState("");

  const runPreview = async () => {
    setBusy(true);
    setPreview(null);
    try {
      const { data } = await api.post("/admin/caul/sweep/run?dry_run=true");
      setPreview(data);
      toast.success(
        `Preview ready — would sweep ${data.swept || 0} users (${data.skipped || 0} skipped, ${data.errored || 0} errored)`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sweep preview failed");
    } finally { setBusy(false); }
  };
  const execute = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/caul/sweep/run?dry_run=false");
      setPreview(data);
      setConfirmOpen(false);
      setConfirmTyped("");
      toast.success(`Swept ${data.swept || 0} users on-chain`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sweep failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-secondary/15 bg-card/40 p-5 space-y-4" data-testid="caul-sweep-card">
      <div className="flex items-center gap-2">
        <Zap className="w-4 h-4 text-secondary" />
        <h3 className="font-serif text-xl">Manual sweep</h3>
      </div>
      <p className="text-foreground/60 text-sm">
        The daily cron runs every night at 02:00 UTC and pays out all pending CAUL rewards to users with linked Xaman wallets + active trustlines.
        Use this to fire one off-cycle (e.g. after fixing a payout issue or before a marketing push). Dry-run first to see exactly who would be paid.
      </p>
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={runPreview}
          disabled={busy}
          className="px-4 py-2 text-xs uppercase tracking-[0.22em] border border-secondary/30 text-secondary hover:bg-secondary/10 disabled:opacity-50"
          data-testid="caul-sweep-preview-btn"
        >
          {busy && !preview ? "Loading…" : "Preview sweep (dry run)"}
        </button>
        <button
          onClick={() => setConfirmOpen(true)}
          disabled={!preview || preview.swept === 0 || busy}
          className="px-4 py-2 text-xs uppercase tracking-[0.22em] bg-secondary text-background hover:bg-secondary/90 disabled:opacity-40"
          data-testid="caul-sweep-execute-btn"
        >
          Execute sweep
        </button>
      </div>

      {preview && (
        <div className="border border-secondary/15 bg-background/40 p-4 mt-2 space-y-3" data-testid="caul-sweep-result">
          <div className="grid grid-cols-4 gap-3 text-center">
            <SmallStat label="Eligible" value={preview.totalUsers} />
            <SmallStat label={preview.dryRun ? "Would sweep" : "Swept"} value={preview.swept || 0} ring="good" />
            <SmallStat label="Skipped" value={preview.skipped || 0} />
            <SmallStat label="Errored" value={preview.errored || 0} ring={(preview.errored || 0) > 0 ? "bad" : ""} />
          </div>
          {preview.results?.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="caul-sweep-result-table">
                <thead className="text-foreground/50 text-[10px] uppercase tracking-[0.22em]">
                  <tr>
                    <th className="text-left px-3 py-2">User</th>
                    <th className="text-right px-3 py-2">Amount</th>
                    <th className="text-left px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.results.map((r, i) => (
                    <tr key={i} className="border-t border-secondary/10">
                      <td className="px-3 py-2 font-mono text-xs">{r.xrplAddress ? `${r.xrplAddress.slice(0,8)}…` : r.userId.slice(0,8)+"…"}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{r.total ?? "—"}</td>
                      <td className="px-3 py-2 text-xs">
                        {r.ok && <span className="text-emerald-400">✓ {r.txHash ? r.txHash.slice(0,10)+"…" : "swept"}</span>}
                        {r.skipped && <span className="text-foreground/40">{r.skipped}</span>}
                        {r.error && <span className="text-rose-400" title={r.error}>error</span>}
                        {r.dryRun && !r.ok && <span className="text-secondary">would sweep</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {confirmOpen && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="caul-sweep-confirm-modal">
          <div className="border border-secondary/40 bg-card max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-2 text-secondary">
              <Zap className="w-5 h-5" />
              <h3 className="font-serif text-2xl">Execute on-chain sweep?</h3>
            </div>
            <p className="text-foreground/80 text-sm">
              This will submit <b>{preview?.swept || 0}</b> XRPL Payment transactions from your hot wallet, paying out a total of {" "}
              <b>{preview?.results?.reduce((s, r) => s + (r.total || 0), 0).toLocaleString()} CAUL</b>. Tx fees come out of the hot wallet XRP balance.
            </p>
            <div>
              <label className="text-foreground/60 text-xs uppercase tracking-[0.22em] block mb-2">
                Type <span className="text-secondary font-mono">SWEEP</span> to confirm
              </label>
              <input
                type="text"
                value={confirmTyped}
                onChange={(e) => setConfirmTyped(e.target.value)}
                className="w-full bg-background border border-secondary/30 text-foreground px-3 py-2 font-mono"
                data-testid="caul-sweep-confirm-input"
                autoFocus
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setConfirmOpen(false); setConfirmTyped(""); }} className="px-4 py-2 text-xs uppercase tracking-wider border border-secondary/20 text-foreground/70 hover:bg-secondary/10" data-testid="caul-sweep-confirm-cancel">
                Cancel
              </button>
              <button onClick={execute} disabled={confirmTyped !== "SWEEP" || busy} className="px-4 py-2 text-xs uppercase tracking-wider bg-secondary text-background hover:bg-secondary/90 disabled:opacity-40" data-testid="caul-sweep-confirm-execute">
                {busy ? "Sweeping…" : "Yes, sweep on-chain"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AmlScreenCard() {
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!name.trim()) { toast.error("Enter a full name"); return; }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/aml/screen", {
        fullName: name.trim(),
        country: country.trim() || undefined,
      });
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Screening failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-secondary/15 bg-card/40 p-5 space-y-4" data-testid="caul-aml-card">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-secondary" />
        <h3 className="font-serif text-xl">AML / sanctions screening</h3>
      </div>
      <p className="text-foreground/60 text-sm">
        Free-tier OpenSanctions search across 240+ official lists — OFAC SDN, EU, UN, UK, and global PEP records.
        Run before approving any provider cashout.
      </p>
      <div className="grid md:grid-cols-3 gap-2">
        <input
          placeholder="Full name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="md:col-span-2 bg-background border border-secondary/20 px-3 py-2 text-sm"
          data-testid="caul-aml-name"
        />
        <input
          placeholder="Country (optional)"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          className="bg-background border border-secondary/20 px-3 py-2 text-sm"
          data-testid="caul-aml-country"
        />
      </div>
      <button
        onClick={run}
        disabled={busy}
        className="px-4 py-2 text-xs uppercase tracking-[0.22em] bg-secondary text-background hover:bg-secondary/90 disabled:opacity-50 flex items-center gap-2"
        data-testid="caul-aml-run-btn"
      >
        <Search className="w-3 h-3" />
        {busy ? "Screening…" : "Run screening"}
      </button>

      {result && (
        <div
          className={`border p-4 mt-2 ${result.hit ? "border-rose-500/40 bg-rose-500/5" : "border-emerald-500/30 bg-emerald-500/5"}`}
          data-testid="caul-aml-result"
        >
          <div className={`flex items-center gap-2 ${result.hit ? "text-rose-300" : "text-emerald-300"}`}>
            {result.hit ? <ShieldAlert className="w-5 h-5" /> : <Check className="w-5 h-5" />}
            <span className="font-serif text-xl">
              {result.hit ? `${result.matches.length} match(es) found` : "No sanctions hit"}
            </span>
          </div>
          {result.hit && (
            <div className="mt-3 space-y-2">
              {result.matches.slice(0, 5).map((m, i) => (
                <div key={i} className="border-t border-rose-500/20 pt-2 text-sm">
                  <div className="flex items-center justify-between flex-wrap">
                    <div>
                      <b className="text-foreground/90">{m.name}</b>{" "}
                      <span className="text-foreground/40 text-xs">score {Math.round((m.score || 0) * 100)}%</span>
                    </div>
                    <div className="text-foreground/40 text-[10px] font-mono">{m.id}</div>
                  </div>
                  <div className="text-foreground/50 text-xs mt-1">{m.datasets?.slice(0, 6).join(" · ")}{m.datasets?.length > 6 ? ` · +${m.datasets.length - 6} more` : ""}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, subtitle, ring, testid }) {
  const cls = ring === "good" ? "border-emerald-500/30"
            : ring === "bad"  ? "border-rose-500/40"
            : "border-secondary/15";
  return (
    <div className={`border ${cls} bg-background/40 p-3`} data-testid={testid}>
      <div className="text-foreground/50 text-[10px] uppercase tracking-[0.22em]">{label}</div>
      <div className="font-serif text-2xl mt-1">{value}</div>
      {subtitle && <div className="text-foreground/40 text-xs mt-0.5">{subtitle}</div>}
    </div>
  );
}

function SmallStat({ label, value, ring }) {
  const cls = ring === "good" ? "text-emerald-400" : ring === "bad" ? "text-rose-400" : "text-foreground";
  return (
    <div>
      <div className={`font-serif text-2xl ${cls}`}>{value}</div>
      <div className="text-foreground/50 text-[10px] uppercase tracking-[0.22em]">{label}</div>
    </div>
  );
}
